import * as React from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link} from 'react-router-dom';
import {Plus} from 'lucide-react';
import {OpsReservationsPageQuery} from './__generated__/OpsReservationsPageQuery.graphql';
import {formatDateTime, primaryButton, statusClass, statusLabel} from './lib';

const QUERY = graphql`
    query OpsReservationsPageQuery($statuses: [ReservationStatus!]) {
        reservations(statuses: $statuses, first: 200) {
            nodes {
                id
                status
                startsAt
                endsAt
                days
                quotedTotal
                customer { name }
                lines { asset { assetTag name } }
                holds { picked returned }
            }
        }
    }`;

const SECTIONS: { title: string; statuses: string[] }[] = [
    {title: 'Requests awaiting confirmation', statuses: ['REQUESTED']},
    {title: 'Out now', statuses: ['CHECKED_OUT']},
    {title: 'Being picked', statuses: ['PICKED']},
    {title: 'Upcoming', statuses: ['CONFIRMED']},
    {title: 'Drafts', statuses: ['DRAFT']},
];

const OpsReservationsPage: React.FC = () => {
    const data = useLazyLoadQuery<OpsReservationsPageQuery>(QUERY, {}, {fetchPolicy: 'network-only'});
    const rows = data.reservations.nodes;

    return (
        <div>
            <div className="flex items-end justify-between mb-6">
                <h1 className="text-3xl font-display font-bold">Reservations</h1>
                <Link to="/ops/reservations/new" className={`${primaryButton} flex items-center gap-2`}>
                    <Plus className="w-4 h-4"/> New reservation
                </Link>
            </div>

            {rows.length === 0 && <p className="text-gray-500 text-sm">No active reservations.</p>}

            {SECTIONS.map(({title, statuses}) => {
                const group = rows.filter((r) => statuses.includes(r.status));
                if (group.length === 0) return null;
                return (
                    <section key={title} className="mb-8">
                        <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">{title}</h2>
                        <div className="border border-gray-800 divide-y divide-gray-900">
                            {group.map((r) => {
                                const picked = r.holds.filter((h) => h.picked).length;
                                const returned = r.holds.filter((h) => h.returned).length;
                                return (
                                    <Link key={r.id} to={`/ops/reservations/${encodeURIComponent(r.id)}`}
                                          className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-4 py-3 hover:bg-white/[0.03]">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 border w-fit ${statusClass[r.status]}`}>
                                            {statusLabel(r.status)}
                                        </span>
                                        <span className="text-white font-semibold md:w-48 truncate">{r.customer.name}</span>
                                        <span className="text-gray-400 text-sm md:w-72">
                                            {formatDateTime(r.startsAt)} → {formatDateTime(r.endsAt)}
                                            <span className="text-gray-600"> · {r.days}d</span>
                                        </span>
                                        <span className="text-gray-500 text-xs flex-1 truncate">
                                            {r.lines.map((l) => l.asset.name).join(', ')}
                                        </span>
                                        <span className="text-xs text-gray-500 whitespace-nowrap">
                                            {r.status === 'CHECKED_OUT'
                                                ? `${returned}/${r.holds.length} back`
                                                : `${picked}/${r.holds.length} picked`}
                                        </span>
                                        {r.quotedTotal && (
                                            <span className="text-lumina-accent font-display text-sm whitespace-nowrap">${r.quotedTotal}</span>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default OpsReservationsPage;
